import { useMutation } from '@apollo/client';
import { IonRouterLink } from '@ionic/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import AutosaveInput from '@components/fieldsNative/AutosaveInput';
import CareProfileBasicForm from '@components/forms/CareProfileBasicForm';
import CareProfileCareNeedsForm from '@components/forms/CareProfileCareNeedsForm';
import CareProfileGuidanceTypeForm from '@components/forms/CareProfileGuidanceTypeForm';
import CareProfileLanguageInterpretationForm from '@components/forms/CareProfileLanguageInterpretationForm';
import CareProfileNotesForm from '@components/forms/CareProfileNotesForm';
import CareProfileUrgencyForm from '@components/forms/CareProfileUrgencyForm';
import InitialConsultBasicInfoForm from '@components/forms/InitialConsultBasicInfoForm';
import InitialConsultClientInfoForm from '@components/forms/InitialConsultClientInfoForm';
import SingleQuestionAutosaveMultiselect from '@components/forms/SingleQuestionAutosaveMultiselect';
import SingleQuestionAutosaveRadio from '@components/forms/SingleQuestionAutosaveRadio';
import InitialConsultQuestion from '@components/InitialConsultQuestion';
import EditableSection from '@components/layout/EditableSection';
import Modal, { ModalElement } from '@components/Modal';
import Financial from '@components/profile/expert/Financial';
import Functional from '@components/profile/expert/Functional';
import Legal from '@components/profile/expert/Legal';
import Medical from '@components/profile/expert/Medical';
import Social from '@components/profile/expert/Social';
import Stage from '@components/profile/expert/Stage';
import Wellbeing from '@components/profile/expert/Wellbeing';
import styles from '@components/profile/Profile.module.css';
import Icon from '@components/text/Icon';
import BodyCopy from '@components/typography/BodyCopy';
import Heading2 from '@components/typography/Heading2';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import {
  CareProfileCareNeedEnum,
  CareProfileGuidanceTypeEnum,
  CareProfileReligionEnum,
  CareProfileUpdateDocument,
  CareProfileUpdateMutation,
  CareProfileUpdateMutationVariables,
  CareProfileUrgencyEnum,
  ClientTopicOfInterestEnum,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { InitialConsultFields } from '@typing/Models';
import { languageNameForCode } from '@utils/languageUtils';
import { optionsForCareNeeds, optionsForGuidanceType, optionsForUrgency } from '@utils/optionUtils';

type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'];
};

const BasicInfo = ({ journey }: Props) => {
  const intl = useIntl();
  const expertBasicInfoOpener = useOpener();

  const careNeedsOpener = useOpener();
  const urgencyOpener = useOpener();
  const myExpert = useMyExpert();
  const guidanceTypeOpener = useOpener();
  const [updateCareProfile] = useMutation<CareProfileUpdateMutation, CareProfileUpdateMutationVariables>(
    CareProfileUpdateDocument
  );

  const basicModalOpener = useOpener();
  const careNeedsModalOpener = useOpener();
  const guidanceTypeModalOpener = useOpener();
  const initialConsultOpener = useOpener();
  const initialConsultClientOpener = useOpener();
  const languageInterpretationModalOpener = useOpener();
  const notesModalOpener = useOpener();
  const urgencyModalOpener = useOpener();

  const basicModalRef = useRef<ModalElement>(null);
  const careNeedsModalRef = useRef<ModalElement>(null);
  const guidanceTypeModalRef = useRef<ModalElement>(null);
  const languageInterpretationModalRef = useRef<ModalElement>(null);
  const notesModalRef = useRef<ModalElement>(null);
  const urgencyModalRef = useRef<ModalElement>(null);

  const [editingInitialConsult, setEditingInitialConsult] = useState<InitialConsultFields[] | undefined>(undefined);
  const [careNeedsOther, setCareNeedsOther] = useState<string | null | undefined>(journey.careProfile.careNeeds?.other);

  const handleBasicError = useCallback(() => {
    if (basicModalRef.current) {
      basicModalRef.current.scrollToTop();
    }
  }, [basicModalRef]);

  const handleCareNeedsError = useCallback(() => {
    if (careNeedsModalRef.current) {
      careNeedsModalRef.current.scrollToTop();
    }
  }, [careNeedsModalRef]);

  const handleGuidanceTypeError = useCallback(() => {
    if (guidanceTypeModalRef.current) {
      guidanceTypeModalRef.current.scrollToTop();
    }
  }, [guidanceTypeModalRef]);

  const handleLanguageInterpretationError = useCallback(() => {
    if (languageInterpretationModalRef.current) {
      languageInterpretationModalRef.current.scrollToTop();
    }
  }, [languageInterpretationModalRef]);

  const handleNotesError = useCallback(() => {
    if (notesModalRef.current) {
      notesModalRef.current.scrollToTop();
    }
  }, [notesModalRef]);

  const handleUrgencyError = useCallback(() => {
    if (urgencyModalRef.current) {
      urgencyModalRef.current.scrollToTop();
    }
  }, [urgencyModalRef]);

  const handleEditInitialConsultQuestion = useCallback(
    (field: InitialConsultFields) => () => {
      setEditingInitialConsult([field]);
      initialConsultOpener.open();
    },
    [initialConsultOpener]
  );

  const handleEditInitialConsultClientQuestion = useCallback(
    (field: InitialConsultFields) => () => {
      setEditingInitialConsult([field]);
      initialConsultClientOpener.open();
    },
    [initialConsultClientOpener]
  );

  const sanitizedNotes = useSanitizeHtml(journey.careProfile.notes, journey.id, journey.careProfile);

  const handleSelectCareProfileCareNeeds = useCallback(
    (careNeedsOptions: CareProfileCareNeedEnum[]) =>
      updateCareProfile({
        variables: {
          careNeeds: {
            careNeedsOptions
          },
          id: journey.careProfile.id
        }
      }),
    [journey.careProfile.id, updateCareProfile]
  );

  const handleSelectCareProfileUrgency = useCallback(
    (urgency: CareProfileUrgencyEnum) =>
      updateCareProfile({
        variables: {
          id: journey.careProfile.id,
          urgency
        }
      }),
    [journey.careProfile.id, updateCareProfile]
  );

  const handleSelectCareProfileGuidanceType = useCallback(
    (guidanceType: CareProfileGuidanceTypeEnum) =>
      updateCareProfile({
        variables: {
          guidanceType,
          id: journey.careProfile.id
        }
      }),
    [journey.careProfile.id, updateCareProfile]
  );

  useEffect(() => {
    if (careNeedsOther && careNeedsOther !== journey.careProfile.careNeeds?.other) {
      updateCareProfile({
        variables: {
          careNeeds: {
            careNeedsOptions: journey.careProfile.careNeeds?.careNeedsOptions ?? [],
            other: careNeedsOther
          },
          id: journey.careProfile.id
        }
      });
    }
  }, [
    careNeedsOther,
    journey.careProfile.careNeeds?.careNeedsOptions,
    journey.careProfile.careNeeds?.other,
    journey.careProfile.id,
    updateCareProfile
  ]);

  return (
    <div>
      <EditableSection descriptionKey="pages.profile.basicInfoDescription" titleKey="pages.profile.basicInfoTitle">
        <div>
          <div className={styles.fieldGrouping}>
            <SingleQuestionAutosaveMultiselect<CareProfileCareNeedEnum>
              currentSelections={journey.careProfile.careNeeds?.careNeedsOptions ?? []}
              onSelect={handleSelectCareProfileCareNeeds}
              opener={careNeedsOpener}
              options={optionsForCareNeeds(intl)}
              questionKey="pages.careProfile.careNeeds"
            />
            <AutosaveInput
              error={null}
              initialValue={journey.careProfile.careNeeds?.other ?? null}
              label={intl.formatMessage({ id: 'forms.onboarding.careNeeds.careNeedsOtherLabel' })}
              name="other"
              setValue={setCareNeedsOther}
              skipIcon
              value={careNeedsOther}
            />
          </div>
          <div className={styles.fieldGrouping}>
            <SingleQuestionAutosaveRadio<CareProfileUrgencyEnum>
              currentSelection={journey.careProfile.urgency}
              onSelect={handleSelectCareProfileUrgency}
              opener={urgencyOpener}
              options={optionsForUrgency(intl)}
              questionKey="pages.careProfile.urgency"
            />
          </div>
          <div className={styles.fieldGrouping}>
            <SingleQuestionAutosaveRadio<CareProfileGuidanceTypeEnum>
              currentSelection={journey.careProfile.guidanceType}
              onSelect={handleSelectCareProfileGuidanceType}
              opener={guidanceTypeOpener}
              options={optionsForGuidanceType(intl)}
              questionKey="pages.careProfile.guidanceType"
            />
          </div>
        </div>
      </EditableSection>

      {myExpert && (
        <div>
          <EditableSection titleKey="pages.careProfile.expertOnly">
            <IonRouterLink onClick={expertBasicInfoOpener.toggle}>
              <div className={styles.section}>
                <Heading2>
                  <FormattedMessage id="pages.careProfile.basicInfo" />
                </Heading2>
                <Icon
                  ariaLabelKey="accessibility.ariaLabels.toggle"
                  className={styles.toggleOpener}
                  icon={expertBasicInfoOpener.isOpen ? 'chevronUp' : 'chevronDown'}
                />
              </div>
            </IonRouterLink>

            <ExpandableSection isOpen={expertBasicInfoOpener.isOpen}>
              <>
                <div className="ion-margin-top">
                  <EditableSection
                    onEdit={languageInterpretationModalOpener.open}
                    smallSpacing
                    smallTitle
                    titleKey="pages.careProfile.languageInterpretation"
                  >
                    <div>
                      {journey.careProfile.languageCode ? (
                        <FormattedMessage
                          id="models.careProfile.languageInterpretationNeededYes"
                          values={{ language: languageNameForCode(journey.careProfile.languageCode) }}
                        />
                      ) : (
                        <FormattedMessage id="dictionary.no" />
                      )}
                    </div>
                  </EditableSection>
                </div>

                {journey.careProfile.concerns.length > 0 && (
                  <EditableSection smallSpacing smallTitle titleKey="pages.careProfile.primaryAreasOfConcern">
                    <div>
                      {journey.careProfile.concerns.map(concern => (
                        <div key={concern}>
                          <BodyCopy>
                            <FormattedMessage id={`models.focusArea.${concern}`} />
                          </BodyCopy>
                        </div>
                      ))}
                    </div>
                  </EditableSection>
                )}
                {journey.careProfile.needsFromGrayce && journey.careProfile.needsFromGrayce.length > 0 && (
                  <EditableSection smallSpacing smallTitle titleKey="pages.careProfile.currentSituation">
                    <div>
                      {journey.careProfile.needsFromGrayce?.map(need => (
                        <div key={need}>
                          <BodyCopy>
                            <FormattedMessage id={`models.careProfile.needsFromGrayce.${need}`} />
                          </BodyCopy>
                        </div>
                      ))}
                    </div>
                  </EditableSection>
                )}

                <InitialConsultQuestion
                  field={InitialConsultFields.MARITAL_STATUS}
                  handleEdit={handleEditInitialConsultQuestion}
                  selections={[
                    journey.careProfile.maritalStatus
                      ? intl.formatMessage({
                          id: `models.initialConsultReport.questions.marital_status.options.${journey.careProfile.maritalStatus}.content`
                        })
                      : null
                  ]}
                  titleKey="models.initialConsultReport.questions.marital_status.content"
                />
                <InitialConsultQuestion
                  field={InitialConsultFields.CLIENT_CULTURAL_CONSIDERATIONS}
                  handleEdit={handleEditInitialConsultClientQuestion}
                  selections={[journey.owner.culturalConsiderations]}
                  titleKey="models.initialConsultReport.questions.client_cultural_considerations.content"
                />
                <InitialConsultQuestion
                  field={InitialConsultFields.CLIENT_IS_VETERAN}
                  handleEdit={handleEditInitialConsultClientQuestion}
                  selections={[
                    journey.owner.isVeteran
                      ? intl.formatMessage({ id: 'dictionary.yes' })
                      : journey.owner.isVeteran === false
                      ? intl.formatMessage({ id: 'dictionary.no' })
                      : null
                  ]}
                  titleKey="models.initialConsultReport.questions.client_is_veteran.content"
                />
                <InitialConsultQuestion
                  field={InitialConsultFields.RELIGION}
                  handleEdit={handleEditInitialConsultQuestion}
                  selections={[
                    journey.careProfile?.religion?.religionOption === CareProfileReligionEnum.other
                      ? journey.careProfile.religion?.other
                      : journey.careProfile.religion?.religionOption
                      ? intl.formatMessage({
                          id: `models.initialConsultReport.questions.religion.options.${journey.careProfile.religion.religionOption}.content`
                        })
                      : null
                  ]}
                  titleKey="models.initialConsultReport.questions.religion.content"
                />
                <InitialConsultQuestion
                  field={InitialConsultFields.CULTURAL_CONSIDERATIONS}
                  handleEdit={handleEditInitialConsultQuestion}
                  selections={[journey.careProfile.culturalConsiderations]}
                  titleKey="models.initialConsultReport.questions.cultural_considerations.content"
                />

                <EditableSection
                  onEdit={notesModalOpener.open}
                  smallSpacing
                  smallTitle
                  titleKey="pages.careProfile.notes"
                >
                  <div>
                    <BodyCopy>{sanitizedNotes}</BodyCopy>
                  </div>
                </EditableSection>

                {myExpert && (
                  <div>
                    <Heading2>{intl.formatMessage({ id: 'pages.careProfile.topicOfInterest' })}</Heading2>
                    <div>
                      <BodyCopy>
                        <FormattedMessage
                          id={`models.client.topicOfInterest.${
                            journey.owner.topicOfInterest ?? ClientTopicOfInterestEnum.UNKNOWN
                          }`}
                        />
                      </BodyCopy>
                    </div>
                  </div>
                )}

                <Modal
                  opener={basicModalOpener}
                  ref={basicModalRef}
                  title={intl.formatMessage({ id: 'models.careProfile.editBasicInfo' })}
                >
                  {({ modalDisplayed }) => (
                    <CareProfileBasicForm
                      autofocus={modalDisplayed}
                      careProfile={journey.careProfile}
                      onCancel={basicModalOpener.close}
                      onError={handleBasicError}
                      onSuccess={basicModalOpener.close}
                    />
                  )}
                </Modal>

                <Modal
                  opener={careNeedsModalOpener}
                  ref={careNeedsModalRef}
                  title={intl.formatMessage({ id: 'models.careProfile.editCareNeeds' })}
                >
                  {() => (
                    <CareProfileCareNeedsForm
                      careProfile={journey.careProfile}
                      onCancel={careNeedsModalOpener.close}
                      onError={handleCareNeedsError}
                      onSuccess={careNeedsModalOpener.close}
                    />
                  )}
                </Modal>
                <Modal
                  opener={initialConsultOpener}
                  title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}
                >
                  {() => (
                    <InitialConsultBasicInfoForm
                      careProfile={journey.careProfile}
                      consultId={journey.initialConsultReport.consult?.id ?? null}
                      initialConsultReportId={journey.initialConsultReport.id}
                      journeyId={journey.id}
                      onCancel={initialConsultOpener.close}
                      onSuccess={initialConsultOpener.close}
                      questions={editingInitialConsult}
                    />
                  )}
                </Modal>
                <Modal
                  opener={initialConsultClientOpener}
                  title={intl.formatMessage({ id: 'models.careProfile.editClientInfo' })}
                >
                  {() => (
                    <InitialConsultClientInfoForm
                      careProfile={journey.careProfile}
                      client={journey.owner}
                      consultId={journey.initialConsultReport.consult?.id ?? null}
                      initialConsultReportId={journey.initialConsultReport.id}
                      onCancel={initialConsultClientOpener.close}
                      onSuccess={initialConsultClientOpener.close}
                      questions={editingInitialConsult}
                    />
                  )}
                </Modal>
                <Modal
                  opener={languageInterpretationModalOpener}
                  ref={languageInterpretationModalRef}
                  title={intl.formatMessage({ id: 'models.careProfile.editLanguageInterpretation' })}
                >
                  {() => (
                    <CareProfileLanguageInterpretationForm
                      careProfile={journey.careProfile}
                      onCancel={languageInterpretationModalOpener.close}
                      onError={handleLanguageInterpretationError}
                      onSuccess={languageInterpretationModalOpener.close}
                    />
                  )}
                </Modal>
                <Modal
                  opener={notesModalOpener}
                  ref={notesModalRef}
                  title={intl.formatMessage({ id: 'models.careProfile.editNotes' })}
                >
                  {({ modalDisplayed }) => (
                    <CareProfileNotesForm
                      autofocus={modalDisplayed}
                      careProfile={journey.careProfile}
                      journeyId={journey.id}
                      onCancel={notesModalOpener.close}
                      onError={handleNotesError}
                      onSuccess={notesModalOpener.close}
                    />
                  )}
                </Modal>

                <Modal
                  opener={urgencyModalOpener}
                  ref={urgencyModalRef}
                  title={intl.formatMessage({ id: 'models.careProfile.editUrgency' })}
                >
                  {() => (
                    <CareProfileUrgencyForm
                      careProfile={journey.careProfile}
                      onCancel={urgencyModalOpener.close}
                      onError={handleUrgencyError}
                      onSuccess={urgencyModalOpener.close}
                    />
                  )}
                </Modal>

                <Modal
                  opener={guidanceTypeModalOpener}
                  ref={guidanceTypeModalRef}
                  title={intl.formatMessage({ id: 'models.careProfile.editGuidanceType' })}
                >
                  {() => (
                    <CareProfileGuidanceTypeForm
                      careProfile={journey.careProfile}
                      onCancel={guidanceTypeModalOpener.close}
                      onError={handleGuidanceTypeError}
                      onSuccess={guidanceTypeModalOpener.close}
                    />
                  )}
                </Modal>
              </>
            </ExpandableSection>

            <Medical journey={journey} />
            <Functional journey={journey} />
            <Legal journey={journey} />
            <Financial journey={journey} />
            <Social journey={journey} />
            <Wellbeing journey={journey} />
            <Stage journey={journey} />
          </EditableSection>
        </div>
      )}
    </div>
  );
};

export default BasicInfo;
